import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Form,
  Input,
  Button,
  Tag,
  Select,
  message,
} from "antd";
import {
  ArrowLeftOutlined,
  PlusOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { Card } from "@/components/ui/card";
import { GOOGLE_TECHNOLOGIES } from "@/data/googleTechnologies";
import { NON_GOOGLE_TECHNOLOGIES } from "@/data/nonGoogleTechnologies";
import { createProject } from "../services/projectService";

const { TextArea } = Input;
const { Option } = Select;

interface SupportingLink {
  url: string;
  description?: string;
}

const AddProject = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [googleTechnologies, setGoogleTechnologies] = useState<string[]>([]);
  const [otherTechnologies, setOtherTechnologies] = useState<string[]>([]);
  const [showGoogleOthersInput, setShowGoogleOthersInput] = useState(false);
  const [showOtherTechOthersInput, setShowOtherTechOthersInput] =
    useState(false);
  const [googleOthersValue, setGoogleOthersValue] = useState("");
  const [otherTechOthersValue, setOtherTechOthersValue] = useState("");
  const [industries, setIndustries] = useState<string[]>([]);
  const [showIndustryOthersInput, setShowIndustryOthersInput] = useState(false);
  const [industryOtherValue, setIndustryOtherValue] = useState("");
  const [supportingLinks, setSupportingLinks] = useState<SupportingLink[]>([
    { url: "", description: "" },
  ]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleGoogleTechChange = (values: string[]) => {
    const hasOthers = values.includes("others");
    const hadOthers = showGoogleOthersInput;
    const filteredValues = values.filter((v) => v !== "others");

    setGoogleTechnologies(filteredValues);
    setShowGoogleOthersInput(hasOthers);

    if (hadOthers && !hasOthers) {
      setGoogleOthersValue("");
      const predefinedTechs = GOOGLE_TECHNOLOGIES;
      const withoutCustom = filteredValues.filter((tech) =>
        predefinedTechs.includes(tech)
      );
      setGoogleTechnologies(withoutCustom);
      form.setFieldsValue({ googleTechnologies: withoutCustom });
    } else {
      form.setFieldsValue({ googleTechnologies: filteredValues });
    }
  };

  const handleOtherTechChange = (values: string[]) => {
    const hasOthers = values.includes("others");
    const hadOthers = showOtherTechOthersInput;
    const filteredValues = values.filter((v) => v !== "others");

    setOtherTechnologies(filteredValues);
    setShowOtherTechOthersInput(hasOthers);

    if (hadOthers && !hasOthers) {
      setOtherTechOthersValue("");
      const predefinedTechs = NON_GOOGLE_TECHNOLOGIES;
      const withoutCustom = filteredValues.filter((tech) =>
        predefinedTechs.includes(tech)
      );
      setOtherTechnologies(withoutCustom);
      form.setFieldsValue({ otherTechnologies: withoutCustom });
    } else {
      form.setFieldsValue({ otherTechnologies: filteredValues });
    }
  };

  const handleGoogleOthersInput = (value: string) => {
    setGoogleOthersValue(value);
    const predefinedTechs = GOOGLE_TECHNOLOGIES;
    const filtered = googleTechnologies.filter((tech) =>
      predefinedTechs.includes(tech)
    );

    if (value.trim()) {
      const customTechs = value
        .split(",")
        .map((tech) => tech.trim())
        .filter((tech) => tech.length > 0);

      const updated = [...filtered, ...customTechs];
      setGoogleTechnologies(updated);
      form.setFieldsValue({ googleTechnologies: updated });
    } else {
      setGoogleTechnologies(filtered);
      form.setFieldsValue({ googleTechnologies: filtered });
    }
  };

  const handleOtherTechOthersInput = (value: string) => {
    setOtherTechOthersValue(value);
    const predefinedTechs = NON_GOOGLE_TECHNOLOGIES;
    const filtered = otherTechnologies.filter((tech) =>
      predefinedTechs.includes(tech)
    );

    if (value.trim()) {
      const customTechs = value
        .split(",")
        .map((tech) => tech.trim())
        .filter((tech) => tech.length > 0);

      const updated = [...filtered, ...customTechs];
      setOtherTechnologies(updated);
      form.setFieldsValue({ otherTechnologies: updated });
    } else {
      setOtherTechnologies(filtered);
      form.setFieldsValue({ otherTechnologies: filtered });
    }
  };

  const handleAddLink = () => {
    setSupportingLinks([...supportingLinks, { url: "", description: "" }]);
  };

  const handleRemoveLink = (index: number) => {
    if (supportingLinks.length > 1) {
      setSupportingLinks(supportingLinks.filter((_, i) => i !== index));
    }
  };

  const handleLinkChange = (
    index: number,
    field: "url" | "description",
    value: string
  ) => {
    const updated = [...supportingLinks];
    updated[index] = { ...updated[index], [field]: value };
    setSupportingLinks(updated);
  };

  const INDUSTRIES = [
    "Agriculture",
    "Healthcare",
    "Education",
    "Infrastructure",
    "Financial Services",
    "Arts & Entertainment",
  ];

  const handleIndustryChange = (values: string[]) => {
    // Filter out "others" option from the actual values
    const filteredValues = values.filter((v) => v !== "others");

    // Update the state
    setIndustries(filteredValues);
    form.setFieldsValue({ industry: filteredValues });
  };

  const handleIndustryOthersInput = (value: string) => {
    setIndustryOtherValue(value);
    // Remove any previous custom entries (those not in INDUSTRIES list)
    const predefinedIndustries = INDUSTRIES;
    const filtered = industries.filter((ind) =>
      predefinedIndustries.includes(ind)
    );

    if (value.trim()) {
      // Parse comma-separated values and trim each
      const customIndustries = value
        .split(",")
        .map((ind) => ind.trim())
        .filter((ind) => ind.length > 0);

      const updated = [...filtered, ...customIndustries];
      setIndustries(updated);
      form.setFieldsValue({ industry: updated });
    } else {
      setIndustries(filtered);
      form.setFieldsValue({ industry: filtered });
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      // Validate supporting links URLs
      const invalidLinks = supportingLinks.filter(
        (link) => link.url.trim() && !link.url.trim().startsWith("https://")
      );
      if (invalidLinks.length > 0) {
        message.error("All supporting link URLs must start with https://");
        return;
      }

      // Validate demo link URL
      if (values.demoLink && !values.demoLink.trim().startsWith("https://")) {
        message.error("Demo video URL must start with https://");
        return;
      }

      // Validate technologies
      if (googleTechnologies.length === 0) {
        message.error("Please select at least one Google technology");
        return;
      }
      if (otherTechnologies.length === 0) {
        message.error("Please select at least one non-Google technology");
        return;
      }

      setIsSubmitting(true);

      // Prepare supporting links (filter out empty ones)
      const filteredLinks = supportingLinks
        .filter((link) => link.url.trim())
        .map((link) => ({
          url: link.url.trim(),
          description: link.description?.trim() || "",
        }));

      // Convert industry array to string for backend (Project model uses string)
      let industryString: string | undefined = undefined;
      if (industries.length > 0) {
        const predefinedIndustries = INDUSTRIES;
        const predefinedIndustryArray = industries.filter((ind) =>
          predefinedIndustries.includes(ind)
        );
        const customIndustries = industries.filter(
          (ind) => !predefinedIndustries.includes(ind)
        );
        
        if (customIndustries.length > 0 || industryOtherValue.trim()) {
          const allIndustries = [...predefinedIndustryArray];
          if (customIndustries.length > 0) {
            allIndustries.push(...customIndustries);
          }
          if (industryOtherValue.trim()) {
            allIndustries.push(industryOtherValue.trim());
          }
          industryString = allIndustries.join(", ");
        } else {
          industryString = predefinedIndustryArray.join(", ");
        }
      }

      const projectData = {
        title: values.title || "",
        description: values.description || "",
        industry: industryString || undefined,
        demoLink: values.demoLink || "",
        documentUrl: values.documentUrl || undefined,
        supportingLinks: filteredLinks.length > 0 ? filteredLinks : undefined,
        googleTechnologies:
          googleTechnologies.length > 0 ? googleTechnologies : undefined,
        otherTechnologies:
          otherTechnologies.length > 0 ? otherTechnologies : undefined,
        hackathonId: values.hackathonId || undefined,
        ownerName: values.ownerName || undefined,
        ownerEmail: values.ownerEmail || undefined,
        documentation: values.documentation || undefined,
      };

      const response = await createProject(projectData);

      if (response.success) {
        message.success("Project created successfully!");
        setTimeout(() => {
          navigate("/projects");
        }, 1500);
      } else {
        message.error(response.message || "Failed to create project");
      }
    } catch (error: any) {
      console.error("Error creating project:", error);

      if (error.errorFields) {
        const firstError = error.errorFields[0];
        message.error(firstError.errors[0]);
      } else {
        const errorMessage =
          error?.response?.data?.message ||
          error?.response?.data?.errors?.[0] ||
          error?.message ||
          "Failed to create project";
        message.error(errorMessage);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <Button
        type="text"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate("/projects")}
        className="mb-4 p-2 text-gray-600 hover:text-gray-900"
      >
        Back
      </Button>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-gray-900">
            Add New Project
          </h1>
        </div>

        <Card className=" shadow-sm onest !p-0 !border-none !bg-transparent !shadow-none">
          <div className="">
            <Form form={form} layout="vertical" className="space-y-6">
              {/* Project Details Section */}
              <div className="space-y-6 bg-white border border-gray-200 rounded-lg p-4">
                <h2 className="text-xl font-semibold text-gray-900 mb-4 border-b border-gray-200 pb-4 onest">
                  Project details
                </h2>

                <Form.Item
                  label={
                    <span className="text-sm font-medium text-gray-700">
                      Project Name <span className="text-red-500">*</span>
                    </span>
                  }
                  name="title"
                  rules={[
                    {
                      required: true,
                      message: "Project name is required",
                    },
                  ]}
                  required={false}
                >
                  <Input
                    placeholder="Enter Project Name"
                    className="rounded-[8px] h-10"
                  />
                </Form.Item>

                <Form.Item
                  label={
                    <div className="flex flex-col gap-1">
                      <span className="text-sm font-medium text-gray-700">
                        Project Description{" "}
                        <span className="text-red-500">*</span>
                      </span>

                      <span className="text-sm text-gray-600 onest">
                        Please describe in 200 words what you built, the problem you solved and the solution you provided.
                      </span>
                    </div>
                  }
                  name="description"
                  rules={[
                    {
                      required: true,
                      message: "Project description is required",
                    },
                    {
                      validator: (_: any, value: string) => {
                        if (!value || value.trim().length === 0) {
                          return Promise.resolve();
                        }
                        if (value.trim().length < 10) {
                          return Promise.reject(
                            new Error(
                              "Project description must be at least 10 characters"
                            )
                          );
                        }
                        const wordCount = value
                          .trim()
                          .split(/\s+/)
                          .filter((word) => word.length > 0).length;
                        if (wordCount > 200) {
                          return Promise.reject(
                            new Error(
                              "Project description must not exceed 200 words"
                            )
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                  required={false}
                >
                  <TextArea
                    rows={4}
                    placeholder="Enter description (minimum 10 characters, maximum 200 words)"
                    className="rounded-[8px] "
                  />
                </Form.Item>

                {/* Industry */}
                <Form.Item
                  label={
                    <span className="text-sm font-medium text-gray-700">
                      What Industry does your project belong to?
                    </span>
                  }
                  name="industry"
                  rules={[
                    {
                      validator: (_: any, value: string[]) => {
                        if ((!value || value.length === 0) && !(showIndustryOthersInput && industryOtherValue.trim())) {
                          return Promise.resolve(); // Industry is optional in admin
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                  required={false}
                >
                  <Select
                    mode="multiple"
                    placeholder="Select industries"
                    value={industries}
                    onChange={handleIndustryChange}
                    className="rounded-[8px] h-11"
                    tagRender={(props) => {
                      const { label, value, closable, onClose } = props;
                      // Don't render tag for "others"
                      if (value === "others") return <></>;
                      return (
                        <Tag
                          closable={closable}
                          onClose={onClose}
                          className="px-3 py-1.5 m-1 rounded-[4px] bg-gray-200 text-gray-700 !text-xs font-medium border-0"
                        >
                          {label}
                        </Tag>
                      );
                    }}
                    dropdownRender={(menu) => (
                      <>
                        {menu}
                        <div className="border-t border-gray-200">
                          <div
                            className={`px-4 py-2 cursor-pointer hover:bg-gray-50 font-medium ${
                              showIndustryOthersInput
                                ? "text-green-600 bg-green-50"
                                : "text-blue-600"
                            }`}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              if (showIndustryOthersInput) {
                                // Deselect "others" - remove custom entries
                                setShowIndustryOthersInput(false);
                                setIndustryOtherValue("");
                                const predefinedIndustries = INDUSTRIES;
                                const withoutCustom = industries.filter((ind) =>
                                  predefinedIndustries.includes(ind)
                                );
                                setIndustries(withoutCustom);
                                form.setFieldsValue({ industry: withoutCustom });
                              } else {
                                // Trigger the "others" selection
                                setShowIndustryOthersInput(true);
                              }
                              // Close the dropdown by blurring
                              setTimeout(() => {
                                const selectElement =
                                  document.activeElement as HTMLElement;
                                if (selectElement) {
                                  selectElement.blur();
                                }
                              }, 100);
                            }}
                          >
                            {showIndustryOthersInput
                              ? "✓ Others (Remove custom input)"
                              : "+ Others (Add custom industry)"}
                          </div>
                        </div>
                      </>
                    )}
                  >
                    {INDUSTRIES.map((industry) => (
                      <Option key={industry} value={industry}>
                        {industry}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>

                {showIndustryOthersInput && (
                  <Form.Item
                    label={
                      <span className="text-sm font-medium text-gray-700">
                        Specify Other Industry
                      </span>
                    }
                  >
                    <Input
                      placeholder="e.g., Retail, Manufacturing"
                      value={industryOtherValue}
                      onChange={(e) =>
                        handleIndustryOthersInput(e.target.value)
                      }
                      className="rounded-[8px] h-11"
                      onBlur={(e) => {
                        if (!e.target.value.trim() && showIndustryOthersInput) {
                          // If input is empty and user clicks away, remove custom entries
                          setIndustryOtherValue("");
                          const predefinedIndustries = INDUSTRIES;
                          const withoutCustom = industries.filter((ind) =>
                            predefinedIndustries.includes(ind)
                          );
                          setIndustries(withoutCustom);
                          form.setFieldsValue({ industry: withoutCustom });
                        }
                      }}
                    />
                  </Form.Item>
                )}

                {/* Owner Name */}
                <Form.Item
                  label={
                    <span className="text-sm font-medium text-gray-700">
                      Owner Name
                    </span>
                  }
                  name="ownerName"
                  rules={[
                    {
                      required: true,
                      message: "Owner name is required",
                    },
                  ]}
                  required={false}
                >
                  <Input
                    placeholder="Enter Owner Name"
                    className="rounded-[8px] h-10"
                  />
                </Form.Item>

                {/* Owner Email */}
                <Form.Item
                  label={
                    <span className="text-sm font-medium text-gray-700">
                      Owner Email
                    </span>
                  }
                  name="ownerEmail"
                  rules={[
                    {
                      required: true,
                      message: "Owner email is required",
                    },
                    {
                      type: "email",
                      message: "Please enter a valid email address",
                    },
                  ]}
                  required={false}
                >
                  <Input
                    placeholder="Enter Owner Email"
                    className="rounded-[8px] h-10"
                  />
                </Form.Item>

                {/* Document URL */}
                <Form.Item
                  label={
                    <span className="text-sm font-medium text-gray-700">
                      Document URL (PDF)
                    </span>
                  }
                  name="documentUrl"
                  rules={[
                    {
                      pattern: /^https:\/\/.+/,
                      message: "URL must start with https://",
                    },
                  ]}
                >
                  <Input
                    placeholder="https://..."
                    className="rounded-[8px] h-10"
                  />
                </Form.Item>


                {/* Technologies */}
                <div className="space-y-6">
                  <Form.Item
                    label={
                      <span className="text-sm font-medium text-gray-700">
                        Google Technologies Used{" "}
                        <span className="text-red-500">*</span>
                      </span>
                    }
                    name="googleTechnologies"
                    rules={[
                      {
                        required: true,
                        message: "Please select at least one Google technology",
                      },
                    ]}
                    required={false}
                  >
                    <Select
                      mode="multiple"
                      placeholder="Select Google technologies you used"
                      value={googleTechnologies}
                      onChange={handleGoogleTechChange}
                      className="rounded-[8px] h-11"
                      tagRender={(props) => {
                        const { label, value, closable, onClose } = props;
                        if (value === "others") return <></>;
                        return (
                          <Tag
                            closable={closable}
                            onClose={onClose}
                            className="px-3 py-1.5 m-1 rounded-[4px] bg-gray-200 text-gray-700 !text-xs font-medium border-0"
                          >
                            {label}
                          </Tag>
                        );
                      }}
                      dropdownRender={(menu) => (
                        <>
                          {menu}
                          <div className="border-t border-gray-200">
                            <div
                              className={`px-4 py-2 cursor-pointer hover:bg-gray-50 font-medium ${
                                showGoogleOthersInput
                                  ? "text-green-600 bg-green-50"
                                  : "text-blue-600"
                              }`}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (showGoogleOthersInput) {
                                  setShowGoogleOthersInput(false);
                                  setGoogleOthersValue("");
                                  const predefinedTechs = GOOGLE_TECHNOLOGIES;
                                  const withoutCustom =
                                    googleTechnologies.filter((tech) =>
                                      predefinedTechs.includes(tech)
                                    );
                                  setGoogleTechnologies(withoutCustom);
                                  form.setFieldsValue({
                                    googleTechnologies: withoutCustom,
                                  });
                                } else {
                                  setShowGoogleOthersInput(true);
                                }
                                setTimeout(() => {
                                  const selectElement =
                                    document.activeElement as HTMLElement;
                                  if (selectElement) {
                                    selectElement.blur();
                                  }
                                }, 100);
                              }}
                            >
                              {showGoogleOthersInput
                                ? "✓ Others (Remove custom input)"
                                : "+ Others (Add custom technology)"}
                            </div>
                          </div>
                        </>
                      )}
                    >
                      {GOOGLE_TECHNOLOGIES.map((tech) => (
                        <Option key={tech} value={tech}>
                          {tech}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>

                  {showGoogleOthersInput && (
                    <Form.Item
                      label={
                        <span className="text-sm font-medium text-gray-700">
                          Specify Other Google Technology
                        </span>
                      }
                    >
                      <Input
                        placeholder="e.g., next js, react js"
                        value={googleOthersValue}
                        onChange={(e) =>
                          handleGoogleOthersInput(e.target.value)
                        }
                        className="rounded-[8px] h-11"
                      />
                    </Form.Item>
                  )}

                  <Form.Item
                    label={
                      <span className="text-sm font-medium text-gray-700">
                        Non-Google/Cloud AI Technologies Used{" "}
                        <span className="text-red-500">*</span>
                      </span>
                    }
                    name="otherTechnologies"
                    rules={[
                      {
                        required: true,
                        message:
                          "Please select at least one non-Google technology",
                      },
                    ]}
                    required={false}
                  >
                    <Select
                      mode="multiple"
                      placeholder="Select non-Google technologies you used"
                      value={otherTechnologies}
                      onChange={handleOtherTechChange}
                      className="rounded-[8px] h-11"
                      tagRender={(props) => {
                        const { label, value, closable, onClose } = props;
                        if (value === "others") return <></>;
                        return (
                          <Tag
                            closable={closable}
                            onClose={onClose}
                            className="px-3 py-1.5 m-1 rounded-[4px] bg-gray-200 text-gray-700 !text-xs font-medium border-0"
                          >
                            {label}
                          </Tag>
                        );
                      }}
                      dropdownRender={(menu) => (
                        <>
                          {menu}
                          <div className="border-t border-gray-200">
                            <div
                              className={`px-4 py-2 cursor-pointer hover:bg-gray-50 font-medium ${
                                showOtherTechOthersInput
                                  ? "text-green-600 bg-green-50"
                                  : "text-blue-600"
                              }`}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (showOtherTechOthersInput) {
                                  setShowOtherTechOthersInput(false);
                                  setOtherTechOthersValue("");
                                  const predefinedTechs =
                                    NON_GOOGLE_TECHNOLOGIES;
                                  const withoutCustom =
                                    otherTechnologies.filter((tech) =>
                                      predefinedTechs.includes(tech)
                                    );
                                  setOtherTechnologies(withoutCustom);
                                  form.setFieldsValue({
                                    otherTechnologies: withoutCustom,
                                  });
                                } else {
                                  setShowOtherTechOthersInput(true);
                                }
                                setTimeout(() => {
                                  const selectElement =
                                    document.activeElement as HTMLElement;
                                  if (selectElement) {
                                    selectElement.blur();
                                  }
                                }, 100);
                              }}
                            >
                              {showOtherTechOthersInput
                                ? "✓ Others (Remove custom input)"
                                : "+ Others (Add custom technology)"}
                            </div>
                          </div>
                        </>
                      )}
                    >
                      {NON_GOOGLE_TECHNOLOGIES.map((tech) => (
                        <Option key={tech} value={tech}>
                          {tech}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>

                  {showOtherTechOthersInput && (
                    <Form.Item
                      label={
                        <span className="text-sm font-medium text-gray-700">
                          Specify Other Technology
                        </span>
                      }
                    >
                      <Input
                        placeholder="e.g., next js, react js"
                        value={otherTechOthersValue}
                        onChange={(e) =>
                          handleOtherTechOthersInput(e.target.value)
                        }
                        className="rounded-[8px] h-11"
                      />
                    </Form.Item>
                  )}
                </div>
              </div>

              {/* Supporting Links Section */}
              <div className="space-y-4 bg-white border border-gray-200 rounded-lg p-4 onest">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  Supporting links
                </h2>

                {supportingLinks.map((link, index) => (
                  <div key={index} className="flex gap-4 items-start !mt-0">
                    <Form.Item
                      label={
                        <span className="text-sm font-medium text-gray-700 !mt-0">
                          Description
                        </span>
                      }
                      name={`supportingLink_description_${index}`}
                      className="flex-1"
                      required={false}
                    >
                      <Input
                        placeholder="e.g., GitHub repository, Documentation, etc."
                        value={link.description || ""}
                        onChange={(e) =>
                          handleLinkChange(index, "description", e.target.value)
                        }
                        className="rounded-[8px] h-10"
                      />
                    </Form.Item>

                    <Form.Item
                      label={
                        <span className="text-sm font-medium text-gray-700 !mt-0">
                          URL
                        </span>
                      }
                      name={`supportingLink_url_${index}`}
                      rules={[
                        {
                          pattern: /^https:\/\/.+/,
                          message: "URL must start with https://",
                        },
                      ]}
                      className="flex-1"
                      required={false}
                    >
                      <Input
                        placeholder="https://github.com/username/"
                        value={link.url || ""}
                        onChange={(e) =>
                          handleLinkChange(index, "url", e.target.value)
                        }
                        className="rounded-[8px] h-10 mb-3"
                      />
                    </Form.Item>
                    {supportingLinks.length > 1 && (
                      <Button
                        type="text"
                        icon={<CloseOutlined />}
                        onClick={() => handleRemoveLink(index)}
                        className="mt-8 text-gray-400 hover:text-red-500"
                      />
                    )}
                  </div>
                ))}

                <Button
                  type="link"
                  icon={<PlusOutlined />}
                  onClick={handleAddLink}
                  className="text-[#1F51D6] bg-[#010ED01A] p-2 oneest font-semibold !text-sm h-auto !mt-4"
                >
                  Add another link
                </Button>
              </div>

              {/* Demo Video Link Section */}
              <div className=" bg-white border border-gray-200 rounded-lg p-4 onest">
                <h2 className="text-xl font-semibold text-gray-900 ">
                  Demo Video Link
                </h2>
                <p className="text-gray-600 text-sm mb-4">
                  Upload your demo video to a google drive and share the link, ensure that the link is publicly available or can be viewed publicly.
                </p>

                <Form.Item
                  label={
                    <span className="text-sm font-medium text-gray-700">
                      Demo Video URL <span className="text-red-500">*</span>
                    </span>
                  }
                  name="demoLink"
                  rules={[
                    { required: true, message: "Demo video URL is required" },
                    {
                      pattern: /^https:\/\/.+/,
                      message: "URL must start with https://",
                    },
                  ]}
                  required={false}
                >
                  <Input
                    placeholder="https://drive.google.com/..."
                    className="rounded-[8px] h-10"
                  />
                </Form.Item>
              </div>

              {/* Project Documentation Section */}
              <div className="bg-white border border-gray-200 rounded-lg p-4 onest">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  Project Documentation
                </h2>
                <p className="text-gray-600 text-sm mb-4">
                  Add detailed project documentation in Markdown format. You can use headings, lists, code blocks, links, and more.
                </p>

                <Form.Item
                  label={
                    <span className="text-sm font-medium text-gray-700">
                      Documentation (Markdown)
                    </span>
                  }
                  name="documentation"
                >
                  <TextArea
                    rows={12}
                    placeholder="# Project Documentation&#10;&#10;## Overview&#10;Describe your project here...&#10;&#10;## Features&#10;- Feature 1&#10;- Feature 2&#10;&#10;## Technical Details&#10;Add technical information here..."
                    className="rounded-[8px] font-mono text-sm"
                  />
                </Form.Item>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-4 pt-6 ">
                <Button
                  onClick={() => navigate("/projects")}
                  className="bg-gray-100 text-gray-700 hover:bg-gray-200 border-0 rounded-[10px] px-4 py-3 h-auto"
                >
                  Cancel
                </Button>
                <Button
                  type="primary"
                  onClick={handleSubmit}
                  loading={isSubmitting}
                  className="bg-[#010ED0] !hover:bg-[#010ED0]/80 border-0 rounded-[10px] px-4 py-3 h-auto text-white"
                >
                  Create Project
                </Button>
              </div>
            </Form>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default AddProject;

